import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { Key, Copy, RefreshCw, ExternalLink, Eye, EyeOff, Zap, MessageSquare, Terminal, Code2, Link2 } from 'lucide-react';
import { mcpKeyService, MCPKey } from '../../services/mcpKeyService';
import { TopNav } from '../ui/top-nav';

export const AIConnectionsPage: React.FC = () => {
  const [mcpKey, setMcpKey] = useState<MCPKey | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [oauthClientId] = useState(() => {
    // Generate a random OAuth Client ID once on mount
    return `bigsplice_${Math.random().toString(36).substring(2, 15)}`;
  });

  useEffect(() => {
    loadKey();
  }, []);

  const loadKey = async () => {
    try {
      const key = await mcpKeyService.getCurrentKey();
      setMcpKey(key);
    } catch (error) {
      // console.error('Error loading MCP key:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    if (mcpKey && !window.confirm('This will revoke your current API key. Continue?')) {
      return;
    }

    setIsGenerating(true);
    try {
      const newKey = await mcpKeyService.generateKey();
      setMcpKey(newKey);
      setShowKey(true);
      toast.success('MCP API Key generated successfully!');
    } catch (error) {
      toast.error('Failed to generate API key');
      // console.error(error);
    } finally {
      setIsGenerating(false);
    }
  };

  const maskKey = (key: string) => {
    if (key.length < 20) return key;
    return `${key.substring(0, 15)}...${key.substring(key.length - 8)}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopNav />
      <main className="overflow-auto p-6 space-y-6" style={{ height: 'calc(100vh - 4.75rem)' }}>
        {/* Header */}
        <div className="flex items-center gap-3">
          <Link2 className="h-5 w-5 text-primary" />
          <div>
            <h1 className="text-base font-semibold text-foreground">AI Connections</h1>
            <p className="text-sm text-muted-foreground">
              Connect BigSplice to Claude, ChatGPT, and Cursor
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* MCP Connection Card */}
            <Card className="border-primary/20 shadow-md overflow-hidden">
              <CardHeader className="bg-muted/30 border-b border-border/50 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Key className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">MCP Server Connection</CardTitle>
                      <CardDescription>
                        Your gateway to connecting AI models with your data
                      </CardDescription>
                    </div>
                  </div>
                  {mcpKey && (
                    <div className="flex items-center space-x-2">
                      <span className="flex h-2 w-2 rounded-full bg-green-500 animate-pulse"></span>
                      <span className="text-xs font-medium text-green-600 dark:text-green-400">Active</span>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-6">
                  {mcpKey ? (
                    <>
                      <div className="grid gap-5">
                        {/* MCP Server URL */}
                        <div className="space-y-2">
                          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            MCP Server URL
                          </Label>
                          <div className="flex space-x-2">
                            <div className="relative flex-1">
                              <Input
                                value="https://mcp.bigsplicer.com/sse"
                                readOnly
                                className="font-mono text-sm bg-muted/50 pr-10"
                              />
                            </div>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => {
                                navigator.clipboard.writeText('https://mcp.bigsplicer.com/sse');
                                toast.success('URL copied!');
                              }}
                              className="shrink-0"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* OAuth Client ID */}
                        <div className="space-y-2">
                          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            OAuth Client ID
                          </Label>
                          <div className="flex space-x-2">
                            <Input
                              value={oauthClientId}
                              readOnly
                              className="font-mono text-sm bg-muted/50"
                            />
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => {
                                navigator.clipboard.writeText(oauthClientId);
                                toast.success('Client ID copied!');
                              }}
                              className="shrink-0"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* API Key */}
                        <div className="space-y-2">
                          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            OAuth Client Secret / API Key
                          </Label>
                          <div className="flex space-x-2">
                            <div className="relative flex-1">
                              <Input
                                value={showKey ? mcpKey.api_key : maskKey(mcpKey.api_key)}
                                readOnly
                                className="font-mono text-sm bg-muted/50 pr-10"
                                type={showKey ? "text" : "password"}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-foreground"
                                onClick={() => setShowKey(!showKey)}
                              >
                                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </Button>
                            </div>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => {
                                navigator.clipboard.writeText(mcpKey.api_key);
                                toast.success('API key copied!');
                              }}
                              className="shrink-0"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>

                      <div className="pt-4 border-t border-border/50 flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">
                          Keep your API key secure. Do not share it publicly.
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleGenerateKey}
                          disabled={isGenerating}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <RefreshCw className={`h-3.5 w-3.5 mr-2 ${isGenerating ? 'animate-spin' : ''}`} />
                          {isGenerating ? 'Regenerating...' : 'Regenerate Key'}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 px-4">
                      <div className="bg-muted/50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                        <Key className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-medium text-foreground mb-2">
                        No API Key Generated
                      </h3>
                      <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                        Generate a secure API key to start connecting BigSplice with your favorite AI tools.
                      </p>
                      <Button
                        onClick={handleGenerateKey}
                        disabled={isGenerating}
                        size="lg"
                        className="min-w-[200px]"
                      >
                        {isGenerating ? (
                          <>
                            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Key className="mr-2 h-4 w-4" />
                            Generate API Key
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Connection Instructions Tabs */}
            {mcpKey && (
              <div className="space-y-4">
                <h2 className="text-2xl font-semibold tracking-tight px-1">Connect Your Tools</h2>
                <Tabs defaultValue="claude-web" className="w-full">
                  <TabsList className="w-full justify-start overflow-x-auto no-scrollbar p-1 mb-4 bg-muted/30 border border-border/50">
                    <TabsTrigger value="claude-web" className="flex-shrink-0">
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2L2 7l10 5 10-5-10-5z" />
                        <path d="M2 17l10 5 10-5" />
                        <path d="M2 12l10 5 10-5" />
                      </svg>
                      Claude.ai
                    </TabsTrigger>
                    <TabsTrigger value="claude-desktop" className="flex-shrink-0">
                      <Terminal className="w-4 h-4 mr-2" />
                      Claude Desktop
                    </TabsTrigger>
                    <TabsTrigger value="cursor" className="flex-shrink-0">
                      <Code2 className="w-4 h-4 mr-2" />
                      Cursor
                    </TabsTrigger>
                    <TabsTrigger value="chatgpt" className="flex-shrink-0">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      ChatGPT
                    </TabsTrigger>
                    <TabsTrigger value="other" className="flex-shrink-0">
                      <Zap className="w-4 h-4 mr-2" />
                      Other Clients
                    </TabsTrigger>
                  </TabsList>

                  {/* Claude Web Content */}
                  <TabsContent value="claude-web" className="mt-0">
                    <Card>
                      <CardHeader>
                        <CardTitle>Connect to Claude.ai</CardTitle>
                        <CardDescription>Use BigSplice directly within the Claude.ai web interface</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-4 text-sm">
                          <div className="flex gap-4 items-start">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-muted font-mono font-medium">1</div>
                            <div className="space-y-1 pt-1">
                              <p className="font-medium">Open Claude Settings</p>
                              <p className="text-muted-foreground">
                                Go to <a href="https://claude.ai/settings" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">Claude.ai Settings</a> and navigate to the <strong>Connectors</strong> section.
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex gap-4 items-start">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-muted font-mono font-medium">2</div>
                            <div className="space-y-1 pt-1">
                              <p className="font-medium">Add Custom Connector</p>
                              <p className="text-muted-foreground">Click "Add custom connector" and enter the following details:</p>
                              <div className="grid gap-3 mt-3 p-4 bg-muted/30 rounded-lg border border-border/50">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                  <span className="text-xs font-semibold uppercase text-muted-foreground md:pt-1">Name</span>
                                  <code className="col-span-2 text-sm bg-background px-2 py-1 rounded border">Bigsplice</code>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                  <span className="text-xs font-semibold uppercase text-muted-foreground md:pt-1">URL</span>
                                  <code className="col-span-2 text-sm bg-background px-2 py-1 rounded border break-all">https://mcp.bigsplicer.com/sse</code>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-4 items-start">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-muted font-mono font-medium">3</div>
                            <div className="space-y-1 pt-1">
                              <p className="font-medium">Configure Authentication</p>
                              <p className="text-muted-foreground">Click "Advanced settings" and add your credentials:</p>
                              <div className="grid gap-3 mt-3 p-4 bg-muted/30 rounded-lg border border-border/50">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                  <span className="text-xs font-semibold uppercase text-muted-foreground md:pt-1">Client ID</span>
                                  <div className="col-span-2 flex items-center gap-2">
                                    <code className="text-sm bg-background px-2 py-1 rounded border flex-1 break-all">{oauthClientId}</code>
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { navigator.clipboard.writeText(oauthClientId); toast.success('Copied!'); }}>
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                  <span className="text-xs font-semibold uppercase text-muted-foreground md:pt-1">Client Secret</span>
                                  <div className="col-span-2 flex items-center gap-2">
                                    <code className="text-sm bg-background px-2 py-1 rounded border flex-1 break-all">
                                      {showKey ? mcpKey.api_key : maskKey(mcpKey.api_key)}
                                    </code>
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { navigator.clipboard.writeText(mcpKey.api_key); toast.success('Copied!'); }}>
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2">
                          <Button asChild>
                            <a href="https://claude.ai/settings" target="_blank" rel="noopener noreferrer">
                              Open Claude.ai Settings <ExternalLink className="ml-2 h-4 w-4" />
                            </a>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Claude Desktop Content */}
                  <TabsContent value="claude-desktop" className="mt-0">
                    <Card>
                      <CardHeader>
                        <CardTitle>Connect to Claude Desktop</CardTitle>
                        <CardDescription>Integrate with the Claude Desktop application on macOS or Windows</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-4 text-sm">
                          <div className="flex gap-4 items-start">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-muted font-mono font-medium">1</div>
                            <div className="space-y-1 pt-1">
                              <p className="font-medium">Locate Config File</p>
                              <p className="text-muted-foreground">Open the configuration file for your operating system:</p>
                              <div className="mt-2 space-y-2">
                                <div className="p-3 bg-muted/30 rounded-lg border border-border/50">
                                  <span className="text-xs font-semibold text-muted-foreground block mb-1">macOS</span>
                                  <code className="text-xs block break-all">~/Library/Application Support/Claude/claude_desktop_config.json</code>
                                </div>
                                <div className="p-3 bg-muted/30 rounded-lg border border-border/50">
                                  <span className="text-xs font-semibold text-muted-foreground block mb-1">Windows</span>
                                  <code className="text-xs block break-all">%APPDATA%\Claude\claude_desktop_config.json</code>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex gap-4 items-start">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-muted font-mono font-medium">2</div>
                            <div className="space-y-1 pt-1 w-full">
                              <p className="font-medium">Add Configuration</p>
                              <p className="text-muted-foreground mb-2">Add the following JSON to your config file:</p>
                              <div className="relative">
                                <pre className="p-4 rounded-lg bg-slate-950 text-slate-50 overflow-x-auto text-xs font-mono border border-slate-800">
{`{
  "mcpServers": {
    "bigsplice": {
      "url": "https://mcp.bigsplicer.com/sse",
      "transport": {
        "type": "sse"
      },
      "env": {
        "BIGSPLICE_API_KEY": "${mcpKey.api_key}"
      }
    }
  }
}`}
                                </pre>
                                <Button 
                                  size="icon" 
                                  variant="secondary" 
                                  className="absolute top-2 right-2 h-8 w-8 bg-slate-800 hover:bg-slate-700 text-slate-200"
                                  onClick={() => {
                                    navigator.clipboard.writeText(`{
  "mcpServers": {
    "bigsplice": {
      "url": "https://mcp.bigsplicer.com/sse",
      "transport": {
        "type": "sse"
      },
      "env": {
        "BIGSPLICE_API_KEY": "${mcpKey.api_key}"
      }
    }
  }
}`);
                                    toast.success('Config copied!');
                                  }}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex gap-4 items-start">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-muted font-mono font-medium">3</div>
                            <div className="space-y-1 pt-1">
                              <p className="font-medium">Restart Claude</p>
                              <p className="text-muted-foreground">Restart the Claude Desktop app to apply changes.</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Cursor Content */}
                  <TabsContent value="cursor" className="mt-0">
                    <Card>
                      <CardHeader>
                        <CardTitle>Connect to Cursor</CardTitle>
                        <CardDescription>Use BigSplice tools directly within the Cursor IDE</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-4 text-sm">
                          <div className="flex gap-4 items-start">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-muted font-mono font-medium">1</div>
                            <div className="space-y-1 pt-1">
                              <p className="font-medium">Open Settings</p>
                              <p className="text-muted-foreground">
                                Open Cursor Settings (<code className="bg-muted px-1 py-0.5 rounded text-xs">Cmd/Ctrl + ,</code>) and navigate to <strong>Features → MCP</strong>.
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex gap-4 items-start">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-muted font-mono font-medium">2</div>
                            <div className="space-y-1 pt-1">
                              <p className="font-medium">Add MCP Server</p>
                              <p className="text-muted-foreground">Click "Add MCP Server" and enter the details:</p>
                              <div className="grid gap-3 mt-3 p-4 bg-muted/30 rounded-lg border border-border/50">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                  <span className="text-xs font-semibold uppercase text-muted-foreground md:pt-1">Name</span>
                                  <code className="col-span-2 text-sm bg-background px-2 py-1 rounded border">bigsplice</code>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                  <span className="text-xs font-semibold uppercase text-muted-foreground md:pt-1">URL</span>
                                  <div className="col-span-2 flex items-center gap-2">
                                    <code className="text-sm bg-background px-2 py-1 rounded border flex-1 break-all">https://mcp.bigsplicer.com/sse</code>
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { navigator.clipboard.writeText('https://mcp.bigsplicer.com/sse'); toast.success('Copied!'); }}>
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                  <span className="text-xs font-semibold uppercase text-muted-foreground md:pt-1">API Key</span>
                                  <div className="col-span-2 flex items-center gap-2">
                                    <code className="text-sm bg-background px-2 py-1 rounded border flex-1 break-all">
                                      {showKey ? mcpKey.api_key : maskKey(mcpKey.api_key)}
                                    </code>
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { navigator.clipboard.writeText(mcpKey.api_key); toast.success('Copied!'); }}>
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* ChatGPT Content */}
                  <TabsContent value="chatgpt" className="mt-0">
                    <Card>
                      <CardHeader>
                        <CardTitle>Connect to ChatGPT</CardTitle>
                        <CardDescription>Add BigSplice as a custom action in your GPTs</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid gap-4 text-sm">
                          <div className="flex gap-4 items-start">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-muted font-mono font-medium">1</div>
                            <div className="space-y-1 pt-1">
                              <p className="font-medium">Create Custom GPT</p>
                              <p className="text-muted-foreground">
                                Go to ChatGPT, create a new GPT (or edit existing), and navigate to <strong>Actions</strong>.
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex gap-4 items-start">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-muted font-mono font-medium">2</div>
                            <div className="space-y-1 pt-1">
                              <p className="font-medium">Configure Action</p>
                              <p className="text-muted-foreground">Add a new action with the server URL:</p>
                              <div className="mt-2 flex items-center gap-2">
                                <code className="text-sm bg-muted/50 px-2 py-1 rounded border flex-1">https://mcp.bigsplicer.com/sse</code>
                                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => { navigator.clipboard.writeText('https://mcp.bigsplicer.com/sse'); toast.success('Copied!'); }}>
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-4 items-start">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-muted font-mono font-medium">3</div>
                            <div className="space-y-1 pt-1">
                              <p className="font-medium">Set Authentication</p>
                              <p className="text-muted-foreground">Select <strong>API Key</strong> authentication and use your key:</p>
                              <div className="mt-2 flex items-center gap-2">
                                <code className="text-sm bg-muted/50 px-2 py-1 rounded border flex-1">
                                  {showKey ? mcpKey.api_key : maskKey(mcpKey.api_key)}
                                </code>
                                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => { navigator.clipboard.writeText(mcpKey.api_key); toast.success('Copied!'); }}>
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Other Clients Content */}
                  <TabsContent value="other" className="mt-0">
                    <Card>
                      <CardHeader>
                        <CardTitle>Generic MCP Client</CardTitle>
                        <CardDescription>Connect any client that supports the Model Context Protocol</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="p-4 bg-muted/30 rounded-lg border border-border/50 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <span className="text-xs font-semibold uppercase text-muted-foreground md:pt-1">Protocol</span>
                            <code className="col-span-2 text-sm">Server-Sent Events (SSE)</code>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <span className="text-xs font-semibold uppercase text-muted-foreground md:pt-1">Endpoint</span>
                            <code className="col-span-2 text-sm break-all">https://mcp.bigsplicer.com/sse</code>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <span className="text-xs font-semibold uppercase text-muted-foreground md:pt-1">Auth Type</span>
                            <code className="col-span-2 text-sm">Bearer Token</code>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <span className="text-xs font-semibold uppercase text-muted-foreground md:pt-1">Token</span>
                            <code className="col-span-2 text-sm break-all">{showKey ? mcpKey.api_key : maskKey(mcpKey.api_key)}</code>
                          </div>
                        </div>
                        
                        <div className="flex justify-center pt-2">
                          <Button variant="link" asChild>
                            <a href="https://modelcontextprotocol.io" target="_blank" rel="noopener noreferrer">
                              Learn more about MCP <ExternalLink className="ml-2 h-3 w-3" />
                            </a>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>

          {/* Sidebar - Right Column */}
          <div className="space-y-6">
            <Card className="!gap-0 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 border-blue-200 dark:border-blue-800/50">
              <CardHeader className="!pb-2">
                <CardTitle className="text-lg flex items-center">
                  <Zap className="h-5 w-5 text-blue-600 mr-2" />
                  Superpowers
                </CardTitle>
                <CardDescription>
                  What you can do with AI connections
                </CardDescription>
              </CardHeader>
              <CardContent className="!pt-0">
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="bg-blue-100 dark:bg-blue-900/50 p-1 rounded mr-3 mt-0.5">
                      <MessageSquare className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <span className="text-sm text-foreground/80">Chat with your data using natural language</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-blue-100 dark:bg-blue-900/50 p-1 rounded mr-3 mt-0.5">
                      <Code2 className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <span className="text-sm text-foreground/80">Auto-generate complex Polars queries</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-blue-100 dark:bg-blue-900/50 p-1 rounded mr-3 mt-0.5">
                      <Terminal className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <span className="text-sm text-foreground/80">Access files directly from your IDE</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="!gap-0">
              <CardHeader className="!pb-2">
                <CardTitle className="text-lg">Security Note</CardTitle>
              </CardHeader>
              <CardContent className="!pt-0">
                <p className="text-sm text-muted-foreground mb-4">
                  Your API key provides access to your BigSplice data. Treat it like a password.
                </p>
                <div className="text-xs bg-muted p-3 rounded border border-border/50">
                  <p className="font-medium mb-1">Best Practices:</p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li>Don't commit keys to git</li>
                    <li>Rotate keys if exposed</li>
                    <li>Use one key per client</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};
